import { useState, useEffect, useImperativeHandle, forwardRef, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useOnboardingDataStore } from '@/stores/onboardingDataStore';
import {
  upsertShootingPreferences,
  uploadCustomFont,
  removeCustomFontByUrl,
  type ThumbnailStyle,
} from '@/services/data/shootingPreferences';
import { Upload, Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

// Import thumbnail images
import boldThumbnail from '@/assets/thumbnails/da-bold.png';
import classiqueThumbnail from '@/assets/thumbnails/da-classique.png';
import customThumbnail from '@/assets/thumbnails/da-custom.png';

interface ThumbnailSelectionStepProps {
  onComplete: () => void;
  setLoading: (loading: boolean) => void;
  onCanProceedChange?: (canProceed: boolean) => void;
  onStyleChange?: (style: ThumbnailStyle | null) => void;
  onFontChange?: (fontUrl: string | null) => void;
}

export interface ThumbnailSelectionStepRef {
  handleContinue: () => void;
}

const thumbnailStyles: { style: ThumbnailStyle; label: string; description: string; image: string | null }[] = [
  {
    style: 'bold',
    label: 'Bold',
    description: 'Design moderne et impactant',
    image: boldThumbnail,
  },
  {
    style: 'classique',
    label: 'Classic',
    description: 'Style épuré et professionnel',
    image: classiqueThumbnail,
  },
  {
    style: 'custom',
    label: 'Custom',
    description: 'Uploadez vos propres typographies',
    image: customThumbnail,
  },
  {
    style: 'none',
    label: 'Aucun',
    description: 'Je veux charger mes\nminiatures moi-même',
    image: customThumbnail,
  },
];

export const ThumbnailSelectionStep = forwardRef<ThumbnailSelectionStepRef, ThumbnailSelectionStepProps>(
  ({ onComplete, setLoading, onCanProceedChange, onStyleChange, onFontChange }, ref) => {
    const { userId } = useAuthStore();
    const shootingPreferences = useOnboardingDataStore((state) => state.shootingPreferences);
    const updateShootingPreferences = useOnboardingDataStore((state) => state.updateShootingPreferences);
    
    // Form state - initialize from preloaded data
    const [thumbnailStyle, setThumbnailStyle] = useState<ThumbnailStyle | null>(
      (shootingPreferences?.thumbnail_style as ThumbnailStyle) || null
    );
    
    // Custom font state - initialize from preloaded data
    const initialFontUrl = shootingPreferences?.custom_font_url || null;
    const [customFontUrl, setCustomFontUrl] = useState<string | null>(initialFontUrl);
    const [customFontName, setCustomFontName] = useState<string | null>(() => {
      if (initialFontUrl) {
        const urlParts = initialFontUrl.split('/');
        const fileName = urlParts[urlParts.length - 1];
        return decodeURIComponent(fileName);
      }
      return null;
    });
    const [isUploadingFont, setIsUploadingFont] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Sync with preloaded data when it becomes available
    useEffect(() => {
      if (shootingPreferences?.thumbnail_style) {
        setThumbnailStyle(shootingPreferences.thumbnail_style as ThumbnailStyle);
      }
      if (shootingPreferences?.custom_font_url) {
        setCustomFontUrl(shootingPreferences.custom_font_url);
        const urlParts = shootingPreferences.custom_font_url.split('/');
        const fileName = urlParts[urlParts.length - 1];
        setCustomFontName(decodeURIComponent(fileName));
        // Notify parent of preloaded font
        onFontChange?.(shootingPreferences.custom_font_url);
      }
    }, [shootingPreferences, onFontChange]);

    // Notify parent of canProceed changes
    useEffect(() => {
      onCanProceedChange?.(thumbnailStyle !== null);
    }, [thumbnailStyle, onCanProceedChange]);

    // Notify parent of style changes for illustration overlay
    useEffect(() => {
      onStyleChange?.(thumbnailStyle);
    }, [thumbnailStyle, onStyleChange]);

    // Handle font file upload
    const handleFontUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !userId) return;

      // Validate file extension
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const validExtensions = ['ttf', 'otf', 'woff', 'woff2'];
      
      if (!validExtensions.includes(fileExt || '')) {
        toast.error('Format non supporté', {
          description: 'Utilisez un fichier .ttf, .otf, .woff ou .woff2'
        });
        return;
      }

      // Validate MIME type
      const validMimeTypes = [
        'font/ttf', 'font/otf', 'font/woff', 'font/woff2',
        'application/x-font-ttf', 'application/x-font-opentype',
        'application/font-woff', 'application/font-woff2',
        'application/octet-stream' // Some browsers report this for fonts
      ];
      
      if (file.type && !validMimeTypes.includes(file.type)) {
        toast.error('Fichier invalide', {
          description: 'Ce fichier ne semble pas être une typographie valide'
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('Fichier trop volumineux', {
          description: 'La taille maximum est de 5 Mo'
        });
        return;
      }

      setIsUploadingFont(true);

      try {
        const uploadResult = await uploadCustomFont(userId, file);
        if (!uploadResult.success || !uploadResult.publicUrl) {
          toast.error('Erreur lors de l\'upload');
          return;
        }
        const fontUrl = uploadResult.publicUrl;
        const result = await upsertShootingPreferences(userId, { custom_font_url: fontUrl });
        if (result.success) {
          setCustomFontUrl(fontUrl);
          setCustomFontName(file.name);
          onFontChange?.(fontUrl);
          toast.success('Typographie uploadée !');
        }
      } catch (error) {
        console.error('Error uploading font:', error);
        toast.error('Erreur lors de l\'upload');
      } finally {
        setIsUploadingFont(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    // Remove uploaded font
    const handleRemoveFont = async () => {
      if (!userId || !customFontUrl) return;

      try {
        await removeCustomFontByUrl(customFontUrl);
        await upsertShootingPreferences(userId, { custom_font_url: null });
        setCustomFontUrl(null);
        setCustomFontName(null);
        onFontChange?.(null);
        toast.success('Typographie supprimée');
      } catch (error) {
        console.error('Error removing font:', error);
      }
    };

    const saveAndContinue = useCallback(async () => {
      if (!userId || !thumbnailStyle) return;
      
      setLoading(true);
      
      // Optimistic update in store
      updateShootingPreferences({ thumbnail_style: thumbnailStyle });
      
      const result = await upsertShootingPreferences(userId, {
        thumbnail_style: thumbnailStyle,
      });
      
      setLoading(false);
      
      if (result.success) {
        onComplete();
      }
    }, [userId, thumbnailStyle, setLoading, onComplete, updateShootingPreferences]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      handleContinue: () => {
        console.log('[ThumbnailSelection] handleContinue called');
        saveAndContinue();
      },
    }), [saveAndContinue]);

    // Render font upload content for Custom style (inline version)
    const renderFontUploadContent = () => {
      return (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept=".ttf,.otf,.woff,.woff2"
            onChange={handleFontUpload}
            className="hidden"
          />
          
          {customFontUrl ? (
            <div className="flex items-center justify-between gap-3 mt-2 pt-2 border-t border-white/10">
              <div className="flex items-center gap-2 min-w-0">
                <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                <span className="text-xs text-white/80 truncate">{customFontName}</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFont();
                }}
                className="p-1 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors flex-shrink-0"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              disabled={isUploadingFont}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 mt-2 rounded-lg border border-dashed border-white/30 hover:border-warning/50 hover:bg-warning/5 transition-all text-white/60 hover:text-white disabled:opacity-50"
            >
              {isUploadingFont ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span className="text-xs">Upload...</span>
                </>
              ) : (
                <>
                  <Upload className="h-3.5 w-3.5" />
                  <span className="text-xs">Uploader typographie</span>
                </>
              )}
            </button>
          )}
        </>
      );
    };

    // Render font upload for mobile (standalone version)
    const renderFontUploadMobile = () => {
      if (thumbnailStyle !== 'custom') return null;

      return (
        <div className="mt-4 p-4 rounded-xl border border-white/20 bg-white/5">
          <input
            ref={fileInputRef}
            type="file"
            accept=".ttf,.otf,.woff,.woff2"
            onChange={handleFontUpload}
            className="hidden"
          />
          
          {customFontUrl ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                <span className="text-sm text-white/80 truncate">{customFontName}</span>
              </div>
              <button
                onClick={handleRemoveFont}
                className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              disabled={isUploadingFont}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-dashed border-white/30 hover:border-warning/50 hover:bg-warning/5 transition-all text-white/60 hover:text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-warning focus:ring-offset-2 focus:ring-offset-black"
            >
              {isUploadingFont ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Upload en cours...</span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span className="text-sm">Uploader votre typographie</span>
                </>
              )}
            </button>
          )}
          <p className="text-xs text-white/40 mt-2">Formats acceptés : .ttf, .otf, .woff, .woff2</p>
        </div>
      );
    };

    return (
      <>
        {/* Desktop: Stacked buttons */}
        <fieldset className="hidden md:block w-full max-w-sm mx-auto">
          <legend className="sr-only">Thumbnail Style Selection</legend>
          <div className="flex flex-col gap-4">
            {thumbnailStyles.map((thumbnail) => (
              <label
                key={thumbnail.style}
                className={cn(
                  "w-full p-4 rounded-xl border-2 transition-all text-left cursor-pointer focus-within:outline-none focus-within:ring-2 focus-within:ring-warning focus-within:ring-offset-2 focus-within:ring-offset-black",
                  thumbnailStyle === thumbnail.style
                    ? "border-warning bg-warning/10"
                    : "border-white/20 bg-white/5 hover:border-white/40"
                )}
              >
                <input
                  type="radio"
                  name="thumbnail-style"
                  value={thumbnail.style}
                  checked={thumbnailStyle === thumbnail.style}
                  onChange={() => setThumbnailStyle(thumbnail.style)}
                  className="sr-only"
                />
                <div className="flex items-center gap-4">
                  {/* Radio button */}
                  <div className={cn(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors pointer-events-none",
                    thumbnailStyle === thumbnail.style
                      ? "border-warning bg-warning"
                      : "border-white/60"
                  )}>
                    {thumbnailStyle === thumbnail.style && (
                      <div className="w-2 h-2 rounded-full bg-black" />
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className={cn(
                      "text-base font-medium transition-colors",
                      thumbnailStyle === thumbnail.style ? "text-warning" : "text-white"
                    )}>
                      {thumbnail.label}
                    </span>
                    <span className="text-sm text-white/60">
                      {thumbnail.description}
                    </span>
                  </div>
                </div>
                {/* Font upload inline when Custom is selected */}
                {thumbnail.style === 'custom' && thumbnailStyle === 'custom' && renderFontUploadContent()}
              </label>
            ))}
          </div>
        </fieldset>

        {/* Mobile: Horizontal carousel with thumbnails - excludes 'custom', shows bold, classique, none */}
        <div className="md:hidden flex-1 flex items-center justify-center -mx-6 w-[calc(100%+3rem)]">
          <div className="flex items-center justify-start gap-4 overflow-x-auto pb-4 snap-x snap-mandatory w-full scroll-pl-6">
            {/* Spacer for initial left alignment */}
            <div className="w-6 flex-shrink-0" aria-hidden="true" />
            {thumbnailStyles
              .filter((t) => t.style !== 'custom')
              .map((thumbnail) => (
              <button
                key={thumbnail.style}
                onClick={() => setThumbnailStyle(thumbnail.style)}
                className="relative flex flex-col items-center flex-shrink-0 snap-start group h-full"
              >
                {/* Image container */}
                <div className={cn(
                  "relative bg-black rounded-xl overflow-hidden transition-all h-full max-h-[50vh] aspect-[9/16]",
                  thumbnailStyle === thumbnail.style
                    ? "opacity-100"
                    : "opacity-80"
                )}>
                  {thumbnail.image ? (
                    <img 
                      src={thumbnail.image} 
                      alt={`Style ${thumbnail.label}`}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-white/5">
                      <span className="text-white/40 text-sm">Pas de miniature</span>
                    </div>
                  )}
                  
                  {/* Label with radio button positioned top-left */}
                  <div className="absolute top-2 left-2 flex items-center gap-2 px-2 py-1 rounded bg-black/50 backdrop-blur-sm">
                    {/* Radio button */}
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors",
                      thumbnailStyle === thumbnail.style 
                        ? "border-warning bg-warning" 
                        : "border-white/60"
                    )}>
                      {thumbnailStyle === thumbnail.style && (
                        <div className="w-1.5 h-1.5 rounded-full bg-black" />
                      )}
                    </div>
                    <div className="flex flex-col items-start">
                      <span className={cn(
                        "text-sm font-medium transition-colors leading-tight text-left",
                        thumbnailStyle === thumbnail.style ? "text-warning" : "text-white/90"
                      )}>
                        {thumbnail.label}
                      </span>
                      <span className="text-xs text-white/60 leading-tight text-left whitespace-pre-line">
                        {thumbnail.description}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
            {/* Spacer for right edge */}
            <div className="w-6 flex-shrink-0" aria-hidden="true" />
          </div>
          
          {/* Font upload section for mobile - shown below carousel */}
          {renderFontUploadMobile()}
        </div>
      </>
    );
  }
);

ThumbnailSelectionStep.displayName = 'ThumbnailSelectionStep';
